import { z } from 'zod';
import { useMutation, useQuery } from '@tanstack/react-query';
import { axiosInstance } from '@/utils/libs/axios';
import { fileSchemaOptional } from '@/utils/libs/schemaUtil';
import { IUserSchema } from './user';

export const commentSchema = z.object({
  id: z.string().optional(),
  status: z.string({ required_error: 'Status is required' }).min(1, 'Status is required'),
  test_issue_id: z.string().optional(),
  comment_summary: z
    .string({ required_error: 'Comment is required' })
    .min(1, 'Comment is required'),
  created_at: z.string().optional(),
  updated_at: z.string().optional(),
  medias: fileSchemaOptional,
});

export type ICommentSchema = z.infer<typeof commentSchema> & {
  created_by?: IUserSchema;
  updated_by?: IUserSchema;
};

export const CommentService = {
  useAllByTestIssueId: (id?: string) =>
    useQuery<ICommentSchema[]>({
      queryKey: ['comment-test-issue', id],
      queryFn: async () => {
        if (!id) return [];

        const response = await axiosInstance.get(`/api/crud/comment`, {
          params: { filter: { test_issue_id: id } },
        });

        return response.data.data.content;
      },
    }),

  useGet: (id?: string) =>
    useQuery<ICommentSchema>({
      queryKey: ['comment', id],
      queryFn: async () => {
        if (!id) return [];

        const response = await axiosInstance.get(`/api/crud/comment/${id}`);
        return response.data.data;
      },
    }),

  useCreate: () =>
    useMutation({
      mutationFn: async (data: ICommentSchema) => {
        const response = await axiosInstance.post('api/crud/comment', data, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
        return response.data;
      },
    }),

  useUpdate: () =>
    useMutation({
      mutationFn: async (data: ICommentSchema) => {
        const response = await axiosInstance.post(
          `/api/crud/comment/${data.id}`,
          data,
        );

        return response.data;
      },
    }),

  useDelete: () =>
    useMutation({
      mutationFn: async (id: string) => {
        const res = await axiosInstance.delete(`/api/crud/comment/${id}`);
        return res.data;
      },
    }),
};
